import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Form, Tabs, Tab, Table } from 'react-bootstrap';
import { Button, ButtonWithLoading } from '@spark/ui-components/components/Button';
import { notification } from '@spark/ui-components/utils/notification';
import {
  downloadPremiacaoImportLayout,
  downloadPremiacaoImportLayoutBlob
} from '../utils/downloadPremiacaoImportLayout';

const PremiacaoImportacaoExcelModal = ({
  show,
  onClose,
  onImportado,
  title,
  layoutColumns,
  layoutObservacoes,
  layoutFileName,
  layoutFileNameComRegistros,
  onImport,
  onDownloadLayoutComRegistros
}) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [downloadingLayout, setDownloadingLayout] = useState(false);
  const [downloadingLayoutComRegistros, setDownloadingLayoutComRegistros] = useState(false);
  const [activeTab, setActiveTab] = useState('upload');
  const [importResult, setImportResult] = useState(null);

  useEffect(() => {
    if (!show) {
      setFile(null);
      setUploading(false);
      setDownloadingLayout(false);
      setDownloadingLayoutComRegistros(false);
      setActiveTab('upload');
      setImportResult(null);
    }
  }, [show]);

  const handleClose = useCallback(() => {
    if (uploading) return;
    onClose?.();
  }, [onClose, uploading]);

  const handleDownloadLayout = useCallback(() => {
    try {
      setDownloadingLayout(true);
      downloadPremiacaoImportLayout(layoutColumns, layoutFileName);
    } catch (error) {
      notification.error('Erro ao baixar layout.');
      console.error('Erro ao baixar layout de premiacao:', error);
    } finally {
      setDownloadingLayout(false);
    }
  }, [layoutColumns, layoutFileName]);

  const handleDownloadLayoutComRegistros = useCallback(async () => {
    if (!onDownloadLayoutComRegistros) {
      notification.error('Exportacao com registros nao configurada.');
      return;
    }

    try {
      setDownloadingLayoutComRegistros(true);
      const response = await onDownloadLayoutComRegistros();
      downloadPremiacaoImportLayoutBlob(response, layoutFileNameComRegistros || layoutFileName);
    } catch (error) {
      notification.error('Erro ao baixar layout com registros.');
      console.error('Erro ao baixar layout com registros de premiacao:', error);
    } finally {
      setDownloadingLayoutComRegistros(false);
    }
  }, [layoutFileName, layoutFileNameComRegistros, onDownloadLayoutComRegistros]);

  const handleSubmit = useCallback(async () => {
    if (!file) {
      notification.error('Selecione um arquivo Excel para importar.');
      return;
    }

    const fileName = file.name.toLowerCase();
    const validExtensions = ['.xls', '.xlsx'];
    const hasValidExtension = validExtensions.some((ext) => fileName.endsWith(ext));

    if (!hasValidExtension) {
      notification.error('Selecione um arquivo Excel (.xls ou .xlsx).');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      const responseData = await onImport(formData);
      setImportResult(responseData);

      const mensagem =
        `Importacao concluida! Total: ${responseData.totalLinhas || 0} | ` +
        `Inseridos: ${responseData.inseridos || 0} | ` +
        `Atualizados: ${responseData.atualizados || 0} | ` +
        `Ignorados: ${responseData.ignorados || 0}` +
        (responseData.totalErros > 0 ? ` | Erros: ${responseData.totalErros}` : '');

      notification.success(mensagem);
      onImportado?.();
    } catch (error) {
      const message = error?.response?.data?.error || error?.message || 'Erro ao importar arquivo.';
      notification.error(message);
      console.error('Erro ao importar premiação:', error);
      setImportResult(null);
    } finally {
      setUploading(false);
    }
  }, [file, onImport, onImportado]);

  const botoesDesabilitados = uploading || downloadingLayoutComRegistros;

  return (
    <Modal show={show} onHide={handleClose} centered backdrop="static" size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Tabs activeKey={activeTab} onSelect={(key) => setActiveTab(key)} className="mb-3">
          <Tab eventKey="upload" title="Upload">
            <Form>
              <Form.Group controlId="premiacaoExcelArquivo" className="mb-3">
                <Form.Label>Arquivo Excel*</Form.Label>
                <Form.Control
                  type="file"
                  accept=".xls,.xlsx"
                  onChange={(event) => {
                    setFile(event.target.files?.[0] || null);
                    setImportResult(null);
                  }}
                  disabled={uploading}
                />
                <Form.Text className="text-muted">
                  Selecione um arquivo Excel (.xls ou .xlsx) com o layout informado na aba Layout.
                </Form.Text>
              </Form.Group>
            </Form>

            {importResult && (
              <div className="mt-4">
                <h6 className="mb-3">Resultado da Importacao</h6>
                <pre
                  style={{
                    backgroundColor: '#f5f5f5',
                    padding: '15px',
                    borderRadius: '4px',
                    overflow: 'auto',
                    maxHeight: '400px',
                    fontSize: '12px',
                    lineHeight: '1.5',
                    border: '1px solid #dee2e6'
                  }}
                >
                  {JSON.stringify(importResult, null, 2)}
                </pre>
              </div>
            )}
          </Tab>

          <Tab eventKey="layout" title="Layout do Excel">
            <div className="mb-3">
              <p className="text-muted">
                O arquivo deve conter as colunas abaixo na primeira linha (cabecalho).
              </p>
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    <th style={{ width: '80px' }}>Posicao</th>
                    <th>Nome da Coluna</th>
                    <th>Descricao</th>
                  </tr>
                </thead>
                <tbody>
                  {layoutColumns.map((col) => (
                    <tr key={col.posicao}>
                      <td className="text-center"><strong>{col.posicao}</strong></td>
                      <td>
                        {col.nome}
                        {col.obrigatorio && <span className="text-danger ms-1">*</span>}
                      </td>
                      <td>{col.descricao}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {layoutObservacoes && layoutObservacoes.length > 0 && (
                <div className="alert alert-info mt-3">
                  <strong>Observacoes:</strong>
                  <ul className="mb-0 mt-2">
                    {layoutObservacoes.map((observacao) => (
                      <li key={observacao}>{observacao}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <ButtonWithLoading
          variant="outline-primary"
          onClick={handleDownloadLayout}
          loading={downloadingLayout ? 'true' : undefined}
          disabled={botoesDesabilitados}
        >
          Baixar Layout
        </ButtonWithLoading>
        <ButtonWithLoading
          variant="outline-primary"
          onClick={handleDownloadLayoutComRegistros}
          loading={downloadingLayoutComRegistros ? 'true' : undefined}
          disabled={botoesDesabilitados}
        >
          Baixar Layout com Registros
        </ButtonWithLoading>
        <Button variant="secondary" onClick={handleClose} disabled={uploading}>
          Cancelar
        </Button>
        <ButtonWithLoading
          variant="primary"
          onClick={handleSubmit}
          loading={uploading ? 'true' : undefined}
        >
          Importar
        </ButtonWithLoading>
      </Modal.Footer>
    </Modal>
  );
};

export default PremiacaoImportacaoExcelModal;
